import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  recordId: number | null;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

async function revertRecord(id: number, reason: string): Promise<unknown> {
  const res = await fetch(buildUrl(api.admin.finances.revert.path, { id }), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? "Failed to revert financial record");
  }
  return res.json();
}

export function RevertFinancialRecordDialog({ open, recordId, onOpenChange, onSuccess }: Props): JSX.Element {
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const revert = useMutation({
    mutationFn: ({ id, r }: { id: number; r: string }) => revertRecord(id, r),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.records.path] });
      queryClient.invalidateQueries({ queryKey: [api.admin.finances.summary.path] });
    },
  });

  function handleConfirm(): void {
    if (!recordId || reason.trim().length < 3) return;
    revert.mutate({ id: recordId, r: reason.trim() }, {
      onSuccess: () => {
        toast({ title: "Pago revertido", description: "El registro volvió a estado pendiente." });
        setReason("");
        onOpenChange(false);
        onSuccess?.();
      },
      onError: (err: Error) => {
        toast({ title: "Error al revertir", description: err.message, variant: "destructive" });
      },
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) setReason(""); onOpenChange(o); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Revertir pago</AlertDialogTitle>
          <AlertDialogDescription>
            Esto marcará el registro como <strong>pendiente</strong>, limpiará la fecha de pago y
            creará un asiento de reversal en la caja.{" "}
            <strong>No emite refund al pagador</strong> — si corresponde, maneja el reembolso fuera del sistema.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-2">
          <Label htmlFor="reversal-reason" className="mb-1 block text-sm font-medium">
            Motivo del reversal <span className="text-destructive">*</span>
          </Label>
          <Textarea id="reversal-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="ej: cobro duplicado, pago aplicado al registro equivocado…" rows={3} className="resize-none" />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={revert.isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            disabled={reason.trim().length < 3 || revert.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {revert.isPending ? "Revirtiendo…" : "Revertir pago"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
